import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface SubCategory {
  id: string;
  name: string;
  budget: number;
  expRomi: string;
  kpi: string;
  description: string;
  expCompletion: string | null;
}

export interface BudgetBucket {
  id: string;
  name: string;
  budget: number;
  allocation: number; // Percentage allocation
  subcategories: SubCategory[];
  available: number; // Available budget that hasn't been allocated to subcategories
  description?: string; // Added description field
}

export const useBudgetData = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalBudget, setTotalBudget] = useState<number>(0);
  const [buckets, setBuckets] = useState<BudgetBucket[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  // Standard bucket categories
  const standardBuckets = [
    "Public Relations (PR)",
    "Analyst Relations (AR)",
    "Field Marketing",
    "MarComms",
    "Digital Marketing",
    "Other Initiatives"
  ];

  // Standard subcategories
  const standardSubcategories: Record<string, string[]> = {
    "Public Relations (PR)": [
      "Press Releases", 
      "Media Relations", 
      "Events & Speaking Engagements", 
      "Crisis Management"
    ],
    "Field Marketing": [
      "Regional Roadshows", 
      "Industry Conferences", 
      "Customer Roundtables", 
      "Sales Enablement Activities"
    ],
    "Industry Conferences": [
      "CES 2025", 
      "Mobile World Congress", 
      "Local Tech Summit"
    ],
    "Digital Marketing": [
      "SEM/PPC",
      "Social Media Ads",
      "Content Marketing",
      "Email Campaigns"
    ],
    "Marcomms": [
      "Website Development",
      "Branding",
      "Creative Services",
      "Marketing Materials"
    ],
    "Analyst Relations (AR)": [
      "Analyst Briefings",
      "Commissioned Research",
      "Analyst Events",
      "Report Subscriptions"
    ],
    "Other Initiatives": [
      "Sponsorships",
      "Product Marketing",
      "Market Research",
      "Partner Marketing"
    ]
  };

  // Get current user ID
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        setUserId(data.user.id);
      }
    };
    
    getCurrentUser();
  }, []);

  // Fetch budget data
  const fetchBudgetData = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      // Fetch overall budget
      const { data: overallData, error: overallError } = await supabase
        .from('marketing_budget')
        .select('*')
        .eq('bucket_name', 'Overall marketing budget')
        .eq('user_id', userId)
        .maybeSingle();

      if (overallError) throw overallError;
      
      if (overallData) {
        setTotalBudget(overallData.bucket_budget);
      } else {
        // No overall budget found, reset everything
        setTotalBudget(0);
        setBuckets([]);
        setLoading(false);
        return;
      }

      // Fetch bucket data
      const { data: bucketData, error: bucketError } = await supabase
        .from('marketing_budget')
        .select('*')
        .neq('bucket_name', 'Overall marketing budget')
        .is('subcategory_name', null)
        .eq('user_id', userId);

      if (bucketError) throw bucketError;

      // Fetch subcategory data - fixing the query syntax here
      const { data: subcategoryData, error: subcategoryError } = await supabase
        .from('marketing_budget')
        .select('*')
        .not('subcategory_name', 'is', null)
        .eq('user_id', userId);

      if (subcategoryError) throw subcategoryError;

      // Process data into buckets and subcategories
      const processedBuckets: BudgetBucket[] = bucketData.map(bucket => {
        const bucketSubcategories = subcategoryData
          .filter(sub => sub.bucket_name === bucket.bucket_name)
          .map(sub => ({
            id: sub.id,
            name: sub.subcategory_name,
            budget: sub.subcategory_budget,
            expRomi: sub.exp_romi || '',
            kpi: sub.kpi || '',
            description: sub.description || '',
            expCompletion: sub.exp_completion
          }));

        const totalSubBudget = bucketSubcategories.reduce((sum, sub) => sum + sub.budget, 0);
        
        return {
          id: bucket.id,
          name: bucket.bucket_name,
          budget: bucket.bucket_budget,
          allocation: overallData ? (bucket.bucket_budget / overallData.bucket_budget) * 100 : 0,
          subcategories: bucketSubcategories,
          available: bucket.bucket_budget - totalSubBudget,
          description: bucket.description
        };
      });

      setBuckets(processedBuckets);
    } catch (err) {
      console.error('Error fetching budget data:', err);
      setError('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial data fetch when userId is available
  useEffect(() => {
    if (userId) {
      fetchBudgetData();
    }
  }, [fetchBudgetData, userId]);

  // Custom setTotalBudget function that also resets buckets when budget is 0
  const setTotalBudgetWithReset = (amount: number) => {
    setTotalBudget(amount);
    if (amount === 0) {
      setBuckets([]);
    }
  };

  // Save overall budget
  const saveOverallBudget = async (amount: number): Promise<void> => {
    if (!userId) {
      toast.error("You need to be logged in to save budget data");
      return;
    }

    try {
      const currentDate = new Date().toISOString().split('T')[0]; // Format as YYYY-MM-DD

      // First check if there's an existing overall budget
      const { data: existingBudget, error: fetchError } = await supabase
        .from('marketing_budget')
        .select('*')
        .eq('bucket_name', 'Overall marketing budget')
        .eq('user_id', userId)
        .maybeSingle();

      if (fetchError) throw new Error("Error fetching budget: " + fetchError.message);

      if (existingBudget) {
        // Update existing overall budget
        const { error: updateError } = await supabase
          .from('marketing_budget')
          .update({ 
            bucket_budget: amount,
            subcategory_name: 'NA',
            subcategory_budget: 0,
            exp_romi: 'NA',
            kpi: 'NA',
            description: 'Overall marketing budget',
            exp_completion: currentDate,
            user_id: userId
          })
          .eq('id', existingBudget.id);

        if (updateError) throw new Error("Error updating budget: " + updateError.message);
      } else {
        // Create new overall budget
        const { error: insertError } = await supabase
          .from('marketing_budget')
          .insert({
            bucket_name: 'Overall marketing budget',
            bucket_budget: amount,
            subcategory_name: 'NA',
            subcategory_budget: 0,
            exp_romi: 'NA',
            kpi: 'NA',
            description: 'Overall marketing budget',
            exp_completion: currentDate,
            user_id: userId
          });

        if (insertError) throw new Error("Error creating budget: " + insertError.message);
      }
      
      setTotalBudget(amount);
      await fetchBudgetData();
    } catch (err) {
      console.error('Error saving overall budget:', err);
      throw err; // Re-throw to allow handling in component
    }
  };

  // Add a new bucket
  const addBucket = async (name: string, budget: number) => {
    if (!userId) {
      toast.error("You need to be logged in to add budget buckets");
      return;
    }
    
    try {
      // Check if budget exceeds total available
      if (budget > totalBudget) {
        toast.error("Budget exceeds total available amount");
        return;
      }

      // Check if this would make bucket allocations exceed total budget
      const currentAllocated = buckets.reduce((sum, bucket) => sum + bucket.budget, 0);
      if (currentAllocated + budget > totalBudget) {
        toast.error("Adding this bucket would exceed total budget");
        return;
      }

      // Add the new bucket
      const { error } = await supabase
        .from('marketing_budget')
        .insert({
          bucket_name: name,
          bucket_budget: budget,
          subcategory_name: null,
          subcategory_budget: null,
          user_id: userId
        });

      if (error) throw error;
      
      // Removed the code that was updating "Overall marketing budget" by reducing it
      // This was causing the logic error where the overall budget was being reduced

      fetchBudgetData();
      toast.success(`Added ${name} bucket successfully`);
    } catch (err) {
      console.error('Error adding bucket:', err);
      toast.error('Failed to add bucket');
    }
  };

  // Update an existing bucket
  const updateBucket = async (id: string, name: string, budget: number, description: string) => {
    if (!userId) {
      toast.error("You need to be logged in to update budget buckets");
      return;
    }
    
    try {
      const bucket = buckets.find(b => b.id === id);
      if (!bucket) {
        toast.error("Bucket not found");
        return;
      }

      // Check if allocated budget would be less than already allocated to subcategories
      const subTotal = bucket.subcategories.reduce((sum, sub) => sum + sub.budget, 0);
      if (budget < subTotal) {
        toast.error("New budget is less than already allocated to subcategories");
        return;
      }

      // Calculate budget difference
      const budgetDiff = budget - bucket.budget;

      // Check if there's enough in the overall budget for an increase
      const { data: overallBudget } = await supabase
        .from('marketing_budget')
        .select('*')
        .eq('bucket_name', 'Overall marketing budget')
        .eq('user_id', userId)
        .maybeSingle();

      if (budgetDiff > 0 && overallBudget && overallBudget.bucket_budget < budgetDiff) {
        toast.error("Not enough budget available for this increase");
        return;
      }

      // Update the bucket
      const { error } = await supabase
        .from('marketing_budget')
        .update({ 
          bucket_name: name,
          bucket_budget: budget,
          description: description,
          user_id: userId
        })
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;

      // Update overall budget accordingly
      if (overallBudget) {
        const { error: updateError } = await supabase
          .from('marketing_budget')
          .update({ bucket_budget: overallBudget.bucket_budget - budgetDiff })
          .eq('id', overallBudget.id)
          .eq('user_id', userId);

        if (updateError) throw updateError;
      }

      fetchBudgetData();
      toast.success(`Updated ${name} bucket successfully`);
    } catch (err) {
      console.error('Error updating bucket:', err);
      toast.error('Failed to update bucket');
    }
  };

  // Delete a bucket
  const deleteBucket = async (id: string) => {
    if (!userId) {
      toast.error("You need to be logged in to delete budget buckets");
      return;
    }
    
    try {
      const bucket = buckets.find(b => b.id === id);
      if (!bucket) {
        toast.error("Bucket not found");
        return;
      }

      // First, delete all subcategories
      const { error: subDeleteError } = await supabase
        .from('marketing_budget')
        .delete()
        .eq('bucket_name', bucket.name)
        .neq('id', id) // Don't delete the bucket itself yet
        .eq('user_id', userId);

      if (subDeleteError) throw subDeleteError;

      // Then delete the bucket
      const { error } = await supabase
        .from('marketing_budget')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;

      // Removing the code that was updating the overall budget
      // This was causing the logic error by adding the deleted bucket's budget back to the overall budget

      fetchBudgetData();
      toast.success(`Deleted ${bucket.name} bucket successfully`);
    } catch (err) {
      console.error('Error deleting bucket:', err);
      toast.error('Failed to delete bucket');
    }
  };

  // Add a new subcategory
  const addSubcategory = async (
    bucketId: string, 
    name: string, 
    budget: number,
    expRomi: string,
    kpi: string,
    description: string,
    expCompletion: string | null
  ) => {
    if (!userId) {
      toast.error("You need to be logged in to add subcategories");
      return;
    }
    
    try {
      const bucket = buckets.find(b => b.id === bucketId);
      if (!bucket) {
        toast.error("Bucket not found");
        return;
      }

      // Check if budget exceeds bucket's available budget
      if (budget > bucket.available) {
        toast.error("Budget exceeds available amount in this bucket");
        return;
      }

      const { error } = await supabase
        .from('marketing_budget')
        .insert({
          bucket_name: bucket.name,
          bucket_budget: 0, // This is a subcategory, not a budget holder
          subcategory_name: name,
          subcategory_budget: budget,
          exp_romi: expRomi,
          kpi: kpi,
          description: description,
          exp_completion: expCompletion,
          user_id: userId
        });

      if (error) throw error;
      
      fetchBudgetData();
      toast.success(`Added ${name} subcategory successfully`);
    } catch (err) {
      console.error('Error adding subcategory:', err);
      toast.error('Failed to add subcategory');
    }
  };

  // Update an existing subcategory
  const updateSubcategory = async (
    bucketId: string,
    subcategoryId: string,
    name: string,
    budget: number,
    expRomi: string,
    kpi: string,
    description: string,
    expCompletion: string | null
  ) => {
    if (!userId) {
      toast.error("You need to be logged in to update subcategories");
      return;
    }
    
    try {
      const bucket = buckets.find(b => b.id === bucketId);
      if (!bucket) {
        toast.error("Bucket not found");
        return;
      }

      const subcategory = bucket.subcategories.find(s => s.id === subcategoryId);
      if (!subcategory) {
        toast.error("Subcategory not found");
        return;
      }

      // Calculate how much more budget we're requesting
      const budgetDiff = budget - subcategory.budget;

      // Check if there's enough available in the bucket
      if (budgetDiff > 0 && bucket.available < budgetDiff) {
        toast.error("Not enough budget available in this bucket");
        return;
      }

      const { error } = await supabase
        .from('marketing_budget')
        .update({
          subcategory_name: name,
          subcategory_budget: budget,
          exp_romi: expRomi,
          kpi: kpi,
          description: description,
          exp_completion: expCompletion,
          user_id: userId
        })
        .eq('id', subcategoryId)
        .eq('user_id', userId);

      if (error) throw error;
      
      fetchBudgetData();
      toast.success(`Updated ${name} subcategory successfully`);
    } catch (err) {
      console.error('Error updating subcategory:', err);
      toast.error('Failed to update subcategory');
    }
  };

  // Delete a subcategory
  const deleteSubcategory = async (bucketId: string, subcategoryId: string) => {
    if (!userId) {
      toast.error("You need to be logged in to delete subcategories");
      return;
    }
    
    try {
      const bucket = buckets.find(b => b.id === bucketId);
      if (!bucket) {
        toast.error("Bucket not found");
        return;
      }

      const subcategory = bucket.subcategories.find(s => s.id === subcategoryId);
      if (!subcategory) {
        toast.error("Subcategory not found");
        return;
      }

      const { error } = await supabase
        .from('marketing_budget')
        .delete()
        .eq('id', subcategoryId)
        .eq('user_id', userId);

      if (error) throw error;
      
      fetchBudgetData();
      toast.success(`Deleted ${subcategory.name} subcategory successfully`);
    } catch (err) {
      console.error('Error deleting subcategory:', err);
      toast.error('Failed to delete subcategory');
    }
  };

  return {
    loading,
    error,
    totalBudget,
    buckets,
    standardBuckets,
    standardSubcategories,
    setTotalBudget: setTotalBudgetWithReset,
    saveOverallBudget,
    addBucket,
    updateBucket,
    deleteBucket,
    addSubcategory,
    updateSubcategory,
    deleteSubcategory
  };
};
